import { useEffect, useState } from "react";
import { Phone, Mail, CalendarClock, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Overline } from "@/components/Reveal";
import { MountReveal, CtaLink } from "@/components/pageKit";
import { useSeo } from "@/lib/useSeo";
import { getContactPage, getSiteSettings } from "@/lib/sanityQueries";
import { peek } from "@/lib/sanity";

// Same-origin by default: the Cloudflare Worker handles /api/contact on easilinked.com.
const API = process.env.REACT_APP_BACKEND_URL || "";

const ContactContent = () => {
    const [data, setData] = useState(() => peek(getContactPage));
    const [settings, setSettings] = useState(() => peek(getSiteSettings));
    const [form, setForm] = useState({ name: "", email: "", company: "", message: "", website: "" });
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error

    useEffect(() => {
        let active = true;
        getContactPage()
            .then((res) => {
                if (active && res) setData(res);
            })
            .catch(() => {});
        getSiteSettings()
            .then((res) => {
                if (active && res) setSettings(res);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    useSeo(data?.seo);

    const d = data || {};
    const email = settings?.primaryEmail;
    const phone = settings?.primaryPhone;

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    // Real server-side submission: POST to /api/contact (Cloudflare Worker), which
    // delivers the inquiry via Resend. No mailto, no local mail client. Success is
    // shown only after the server confirms acceptance.
    const onSubmit = async (e) => {
        e.preventDefault();
        if (status === "submitting") return; // prevent duplicate submissions
        setStatus("submitting");
        try {
            const resp = await fetch(`${API}/api/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!resp.ok) throw new Error("submit_failed");
            setStatus("success");
            setForm({ name: "", email: "", company: "", message: "", website: "" });
        } catch (err) {
            setStatus("error"); // keep entered data for retry
        }
    };

    const inputClass =
        "w-full rounded-xl border border-brand-line bg-brand-bg px-4 py-3 text-sm text-brand-text placeholder:text-brand-muted focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/20 transition-colors duration-150";

    return (
        <>
            {/* HERO */}
            <section
                data-testid="contact-hero"
                className="relative overflow-hidden pt-36 pb-14 sm:pt-44 sm:pb-16"
            >
                <div className="pointer-events-none absolute inset-0 el-grid-bg" aria-hidden />
                <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
                    <MountReveal>
                        <span className="inline-flex items-center">
                            <Overline>Contact</Overline>
                        </span>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <h1
                            data-testid="contact-hero-heading"
                            className="mt-7 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-6xl"
                        >
                            {d.heroHeadline || "Let's Connect You to What's Next."}
                        </h1>
                    </MountReveal>
                    <MountReveal delay={0.1}>
                        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-brand-muted sm:text-lg">
                            {d.heroSupportingMessage}
                        </p>
                    </MountReveal>
                </div>
            </section>

            {/* INTRO + CONTACT METHODS */}
            <section
                data-testid="contact-details"
                className="mx-auto max-w-7xl px-5 py-10 sm:px-8"
            >
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-start">
                    <div className="lg:col-span-5">
                        <MountReveal>
                            <p className="text-base leading-relaxed text-brand-muted sm:text-lg">
                                {d.contactIntro}
                            </p>
                        </MountReveal>
                        <div className="mt-8 space-y-4">
                            {d.showPhone && phone && (
                                <MountReveal>
                                    <a
                                        href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
                                        data-testid="contact-phone"
                                        className="group flex items-center gap-4 rounded-2xl border border-brand-line bg-brand-surface p-5 transition-colors duration-150 hover:border-brand-accent/40"
                                    >
                                        <span className="grid h-11 w-11 place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent">
                                            <Phone className="h-5 w-5" />
                                        </span>
                                        <span>
                                            <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-muted">
                                                Phone
                                            </span>
                                            <span className="mt-1 block text-base font-semibold text-brand-text">
                                                {phone}
                                            </span>
                                        </span>
                                    </a>
                                </MountReveal>
                            )}
                            {d.showEmail && email && (
                                <MountReveal delay={0.05}>
                                    <a
                                        href={`mailto:${email}`}
                                        data-testid="contact-email"
                                        className="group flex items-center gap-4 rounded-2xl border border-brand-line bg-brand-surface p-5 transition-colors duration-150 hover:border-brand-accent/40"
                                    >
                                        <span className="grid h-11 w-11 place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent">
                                            <Mail className="h-5 w-5" />
                                        </span>
                                        <span>
                                            <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-muted">
                                                Email
                                            </span>
                                            <span className="mt-1 block text-base font-semibold text-brand-text">
                                                {email}
                                            </span>
                                        </span>
                                    </a>
                                </MountReveal>
                            )}
                        </div>
                    </div>

                    {/* BOOKING CARD */}
                    <div className="lg:col-span-7">
                        <MountReveal delay={0.05}>
                            <div
                                data-testid="contact-booking"
                                className="relative overflow-hidden rounded-3xl border border-brand-line bg-brand-surface p-8 sm:p-10"
                            >
                                <span className="grid h-12 w-12 place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent">
                                    <CalendarClock className="h-5 w-5" />
                                </span>
                                <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight text-brand-text sm:text-3xl">
                                    {d.bookingHeadline || "Schedule a Conversation"}
                                </h2>
                                <p className="mt-4 text-base leading-relaxed text-brand-muted">
                                    {d.bookingMessage}
                                </p>
                                {d.bookingUrl && (
                                    <div className="mt-8">
                                        <CtaLink
                                            href={d.bookingUrl}
                                            label="Book a time"
                                            openInNewTab
                                            variant="primary"
                                            testid="contact-booking-button"
                                        />
                                    </div>
                                )}
                            </div>
                        </MountReveal>
                    </div>
                </div>
            </section>

            {/* MESSAGE FORM (email-based) */}
            <section
                data-testid="contact-form-section"
                className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-24"
            >
                <MountReveal>
                    <Overline>{d.formHeadline || "Send Us a Message"}</Overline>
                </MountReveal>
                <MountReveal delay={0.05}>
                    <h2 className="mt-5 font-heading text-3xl font-extrabold leading-[1.05] tracking-tight text-brand-text sm:text-4xl">
                        Prefer email? Start here.
                    </h2>
                </MountReveal>
                <MountReveal delay={0.1}>
                    <p className="mt-4 text-base leading-relaxed text-brand-muted">
                        {d.formIntro}
                    </p>
                </MountReveal>
                <MountReveal delay={0.15}>
                    {status === "success" ? (
                        <div
                            data-testid="contact-form-success"
                            className="mt-8 flex items-start gap-4 rounded-2xl border border-brand-accent/40 bg-brand-surface p-6"
                        >
                            <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-accent" />
                            <div>
                                <h3 className="font-heading text-lg font-bold tracking-tight text-brand-text">
                                    Thanks — your message has been sent.
                                </h3>
                                <p className="mt-1 text-sm leading-relaxed text-brand-muted">
                                    Our team will follow up soon.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} data-testid="contact-form" className="mt-8 space-y-4">
                            {/* Honeypot — visually hidden; real users leave it empty. */}
                            <input
                                type="text"
                                name="website"
                                value={form.website}
                                onChange={onChange}
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                className="hidden"
                            />
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <input
                                    name="name"
                                    value={form.name}
                                    onChange={onChange}
                                    required
                                    placeholder="Your name"
                                    data-testid="contact-form-name"
                                    className={inputClass}
                                />
                                <input
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={onChange}
                                    required
                                    placeholder="Work email"
                                    data-testid="contact-form-email"
                                    className={inputClass}
                                />
                            </div>
                            <input
                                name="company"
                                value={form.company}
                                onChange={onChange}
                                placeholder="Company (optional)"
                                data-testid="contact-form-company"
                                className={inputClass}
                            />
                            <textarea
                                name="message"
                                value={form.message}
                                onChange={onChange}
                                required
                                rows={5}
                                placeholder="How can we help?"
                                data-testid="contact-form-message"
                                className={`${inputClass} resize-y`}
                            />

                            {status === "error" && (
                                <div
                                    data-testid="contact-form-error"
                                    className="flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/5 p-4"
                                >
                                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
                                    <p className="text-sm leading-relaxed text-brand-text">
                                        Something went wrong sending your message. Please try again
                                        {email ? (
                                            <>
                                                {" "}
                                                or email us directly at{" "}
                                                <a
                                                    href={`mailto:${email}`}
                                                    className="font-semibold text-brand-accent underline"
                                                >
                                                    {email}
                                                </a>
                                            </>
                                        ) : null}
                                        .
                                    </p>
                                </div>
                            )}

                            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <button
                                    type="submit"
                                    data-testid="contact-form-submit"
                                    disabled={status === "submitting"}
                                    className="group inline-flex items-center gap-2 rounded-control bg-brand-accent px-7 py-3.5 text-sm font-semibold text-brand-on-accent transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {status === "submitting" ? "Sending…" : "Send message"}
                                    <Send className="h-4 w-4 transition-colors duration-150" />
                                </button>
                                <p className="text-xs text-brand-muted">
                                    We'll route your message to our team and follow up by email.
                                </p>
                            </div>
                        </form>
                    )}
                </MountReveal>
            </section>
        </>
    );
};

export default function ContactPage() {
    return (
        <PageLayout>
            <ContactContent />
        </PageLayout>
    );
}
